"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
// Importing necessary dependencies and components for the login page
const react_1 = require("react");
const react_native_1 = require("react-native");
const react_redux_1 = require("react-redux");
const native_1 = require("@react-navigation/native");
const store_1 = require("../../store");
const user_1 = require("../../store/selectors/user");
const appState_1 = require("../../store/selectors/appState");
const user_2 = require("../../slices/user");
const appState_2 = require("../../slices/appState");
const useAppMessage_1 = require("../../hooks/useAppMessage");
const LoginApple_1 = __importDefault(require("../Auth/LoginApple"));
const LoginGoogle_1 = __importDefault(require("../Auth/LoginGoogle"));
const AppText_1 = require("../AppText");
const character_1 = require("../../services/character");
const cache_1 = __importDefault(require("../../helper/cache"));
const colors_1 = require("../../common/colors");
const error_1 = require("../../common/error");
const application_host_1 = require("../../common/application-host");
const AppIcons_1 = require("../AppIcons");
const useDevice_1 = require("../../hooks/useDevice");
const useAnalytics_1 = __importDefault(require("../../hooks/useAnalytics"));
// Defining the main LoginPage component
const LoginPage = ({ route }) => {
    // Setting up hooks and getting necessary data from the store
    const dispatch = (0, store_1.useAppDispatch)();
    const { isSmallScreen } = (0, useDevice_1.useDevice)();
    const { trackUserSignIn } = (0, useAnalytics_1.default)();
    const user = (0, react_redux_1.useSelector)(user_1.selectUser);
    const isLogging = (0, react_redux_1.useSelector)(appState_1.selectIsLogging);
    const { showNotification } = (0, useAppMessage_1.useAppMessage)();
    const navigation = (0, native_1.useNavigation)();
    // Extracting the redirectTo parameter from the route, if it exists
    const { redirectTo } = route.params || {};
    // Defining the login handler function
    const handleLogin = (0, react_1.useCallback)(async (user) => {
        // Dispatching actions to update the app state when login starts
        dispatch((0, user_2.setAuthToken)(await user.accessToken));
        dispatch((0, user_2.setFbUser)({
            ...user,
            ...(!user.displayName && user.providerData[0]),
        }));
        dispatch((0, user_2.resetCurrentConversation)());
        try {
            // Fetching the user profile data
            const userData = await (0, character_1.getUserProfileService)();
            if (userData instanceof error_1.UnauthorizedRequest) {
                // If unauthorized, updating the app state accordingly
                dispatch((0, appState_2.setIsLogging)(false));
                dispatch((0, appState_2.setShouldInsertUsername)(true));
                // navigation.navigate('UsernameInput');
            }
            else if (userData instanceof error_1.RequestError) {
                // If there's an error, logging it
                console.error(userData);
            }
            else {
                // If successful, updating the app state with the user data
                dispatch((0, user_2.setAppUser)({ ...userData.data, env: application_host_1.ENV_TYPE }));
                await cache_1.default.setItem(`appUser_${application_host_1.ENV_TYPE}`, userData.data);
                dispatch((0, appState_2.setIsLogging)(false));
                trackUserSignIn();
                // Navigating to the appropriate screen after login
                if (redirectTo) {
                    navigation.navigate(redirectTo);
                }
                else {
                    navigation.navigate('Home', { screen: 'Documents' });
                }
                // Showing a success notification
                showNotification("Welcome", "Successfully logged in.", "success");
            }
        }
        catch (error) {
            // If there's an error during login, handling it
            console.error("Login error:", error);
            showNotification("Error", "Failed to log in. Please try again.", "error");
        }
        finally {
            // Ensuring that the logging state is set to false when done
            dispatch((0, appState_2.setIsLogging)(false));
        }
    }, [trackUserSignIn, dispatch, navigation, redirectTo]);
    if (user?.type === 'PERMANENT') {
        navigation.navigate('Home', { screen: 'Account' });
        return null;
    }
    ;
    // Rendering the login page UI
    return ((0, jsx_runtime_1.jsx)(react_native_1.SafeAreaView, { style: isSmallScreen ? styles.smallContainer : styles.container, children: (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: isSmallScreen ? styles.smallScreenLoginBox : styles.loginBox, children: [(0, jsx_runtime_1.jsx)(AppIcons_1.SummaraizerLogo, { style: styles.logo }), (0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: isSmallScreen ? styles.smallTitle : styles.title, children: "Welcome to Summaraizer" }), (0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: isSmallScreen ? styles.smallSubtitle : styles.subtitle, children: "Unleash the power of your documents! Create your free account and get 10 summaries right away" }), (0, jsx_runtime_1.jsxs)(AppText_1.AppText, { style: isSmallScreen ? styles.smallTermsText : styles.termsText, children: ["By creating an account you agree with talktoo.ai", '\n', (0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: isSmallScreen ? styles.smallLink : styles.link, onPress: () => react_native_1.Linking.openURL('https://www.makr.ai/terms'), children: "terms of use" }), ' ', "and", ' ', (0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: isSmallScreen ? styles.smallLink : styles.link, onPress: () => react_native_1.Linking.openURL('https://www.makr.ai/privacy-policy'), children: "privacy policy" })] }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.buttonContainer, children: [(0, jsx_runtime_1.jsx)(LoginApple_1.default, { style: styles.button, loginSuccessHandler: handleLogin }), (0, jsx_runtime_1.jsx)(LoginGoogle_1.default, { style: styles.button, loginSuccessHandler: handleLogin })] })] }) }));
};
// Defining the styles for the login page
const styles = react_native_1.StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: `${colors_1.Colors.DARK_700}01`,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    smallContainer: {
        flex: 1,
        backgroundColor: `${colors_1.Colors.DARK_700}01`,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 16,
    },
    loginBox: {
        backgroundColor: colors_1.Colors.DARK_600,
        padding: 30,
        borderRadius: 16,
        width: '100%',
        maxWidth: 464,
        alignItems: 'center',
        borderColor: `${colors_1.Colors.PRIMARY_BLUE}20`,
        borderWidth: 1,
        shadowColor: colors_1.Colors.PRIMARY_BLUE,
        shadowOffset: { width: 2, height: 8 },
        shadowOpacity: 0.16,
        shadowRadius: 24,
    },
    smallScreenLoginBox: {
        backgroundColor: colors_1.Colors.DARK_700,
        padding: 20,
        borderRadius: 16,
        width: '100%',
        maxWidth: 396,
        alignItems: 'center',
        marginHorizontal: 16,
        borderColor: `${colors_1.Colors.PRIMARY_BLUE}20`,
        borderWidth: 1,
        shadowColor: colors_1.Colors.PRIMARY_BLUE,
        shadowOffset: { width: 2, height: 8 },
        shadowOpacity: 0.16,
        shadowRadius: 24,
    },
    logo: {
        alignSelf: "center",
        alignContent: "center",
        alignItems: "center",
        marginBottom: 12,
    },
    title: {
        color: colors_1.Colors.white,
        fontSize: 30,
        fontWeight: '700',
        marginBottom: 10,
        textAlign: 'center',
    },
    smallTitle: {
        color: colors_1.Colors.white,
        fontSize: 20,
        fontWeight: '700',
        marginBottom: 8,
        textAlign: 'center',
    },
    subtitle: {
        color: colors_1.Colors.DARK_100,
        fontSize: 14,
        textAlign: 'center',
        marginBottom: 30,
        paddingHorizontal: 20,
    },
    smallSubtitle: {
        color: colors_1.Colors.DARK_100,
        fontSize: 16,
        textAlign: 'center',
        marginBottom: 24,
        paddingHorizontal: 10,
    },
    buttonContainer: {
        gap: 12,
        width: '100%',
    },
    button: {
        width: '100%',
        borderRadius: 24,
        fontWeight: '200',
        backgroundColor: colors_1.Colors.white,
    },
    termsText: {
        color: colors_1.Colors.DARK_200,
        fontSize: 12,
        textAlign: 'center',
        maxWidth: 408,
        marginBottom: 48,
        paddingHorizontal: 20,
    },
    smallTermsText: {
        color: colors_1.Colors.DARK_200,
        fontSize: 14,
        textAlign: 'center',
        maxWidth: 408,
        marginBottom: 16,
        paddingHorizontal: 10,
    },
    link: {
        color: colors_1.Colors.white,
        fontSize: 12,
        textDecorationLine: 'underline',
    },
    smallLink: {
        color: colors_1.Colors.white,
        fontSize: 14,
        textDecorationLine: 'underline',
    },
});
// Exporting the memoized LoginPage component that can be used inRootStack
exports.default = (0, react_1.memo)(LoginPage);
